import React, { useEffect, useRef, useState } from 'react';
import './style.scss';

interface Props {
    value: string;
    setValue: Function;
    minRows?: number;
    maxRows?: number;
    placeholder?: string;
    disabled?: boolean;
    maxLength?: number;
    submitOnEnter?: boolean;
    onSubmit?: Function;
    countChars?: boolean;
    textAreaClassName?: string;
}

const ResizableTextarea: React.FC<Props> = ({
    minRows = 1,
    maxRows = 10,
    placeholder = '',
    setValue,
    value,
    disabled = false,
    maxLength,
    submitOnEnter = false,
    onSubmit,
    countChars = false,
    textAreaClassName = '',
}: Props) => {
    const [rows, setRows] = useState<number>(minRows);
    const [textareaLineHeight, setTextareaLineHeight] = useState<number>(0);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    let firstLoad = true;

    useEffect(() => {
        if (textAreaRef.current) {
            const lineHeight = window
                .getComputedStyle(textAreaRef.current)
                .getPropertyValue('line-height')
                .replace('px', '');
            setTextareaLineHeight(parseInt(lineHeight));
        }
    }, [textAreaRef.current]);

    useEffect(() => {
        if (firstLoad && textareaLineHeight > 0 && value.length > 0 && textAreaRef.current) {
            firstLoad = false;
            adjustTextareaRows(textAreaRef.current, value);
        }
    }, [textareaLineHeight, value]);

    const adjustTextareaRows = (textareaTarget: HTMLTextAreaElement, newValue: string) => {
        const previousRows = textareaTarget.rows;
        textareaTarget.rows = minRows;

        const currentRows = Math.floor(textareaTarget.scrollHeight / textareaLineHeight);

        if (currentRows === previousRows) {
            textareaTarget.rows = currentRows;
        }

        if (currentRows >= maxRows) {
            textareaTarget.rows = maxRows;
            textareaTarget.scrollTop = textareaTarget.scrollHeight;
        }

        setValue(newValue);
        setRows(currentRows < maxRows ? currentRows : maxRows);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (submitOnEnter && event.keyCode === 13 && !event.shiftKey && onSubmit) {
            event.preventDefault();
            onSubmit();
        }
    };

    useEffect(() => {
        if (value.length === 0) {
            setRows(1);
        }
    }, [value]);

    return (
        <div>
            <textarea
                className={`resizable-textarea ${textAreaClassName}`}
                disabled={disabled}
                maxLength={maxLength}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    adjustTextareaRows(event.target, event.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                ref={textAreaRef}
                rows={rows}
                value={value}
            />
            {countChars && (
                <div className="chars-counter">
                    <span>{value.length}</span>/{maxLength}
                </div>
            )}
        </div>
    );
};

export default ResizableTextarea;
